import {
  CheckCircle,
  Clock,
  Package,
  Truck,
  Wrench,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { formatOrderDimensionLines } from '@/lib/dimensions';

export const STATUSES: Array<{
  value: string;
  label: string;
  glassColor: string;
  textColor: string;
  borderColor: string;
  icon: LucideIcon;
}> = [
  { value: 'pending', label: 'Ожидает', glassColor: 'rgba(246,173,85,0.15)', textColor: '#F6AD55', borderColor: 'rgba(246,173,85,0.35)', icon: Clock },
  { value: 'confirmed', label: 'Подтверждён', glassColor: 'rgba(99,179,237,0.15)', textColor: '#63B3ED', borderColor: 'rgba(99,179,237,0.35)', icon: CheckCircle },
  { value: 'in_production', label: 'В производстве', glassColor: 'rgba(159,122,234,0.15)', textColor: '#B794F4', borderColor: 'rgba(159,122,234,0.35)', icon: Wrench },
  { value: 'ready', label: 'Готов', glassColor: 'rgba(72,187,120,0.15)', textColor: '#68D391', borderColor: 'rgba(72,187,120,0.35)', icon: Package },
  { value: 'delivered', label: 'Закрыт · выдан', glassColor: 'rgba(255,255,255,0.06)', textColor: '#A0AEC0', borderColor: 'rgba(255,255,255,0.12)', icon: Truck },
  { value: 'cancelled', label: 'Отменён', glassColor: 'rgba(252,129,74,0.15)', textColor: '#FC814A', borderColor: 'rgba(252,129,74,0.35)', icon: XCircle },
];

export const ORDER_NOTE_TEMPLATES = [
  'Заказ подтвержден. Передали в производство.',
  'Согласуем удобную дату монтажа. Напишем сегодня.',
  'Изделие готово. Доступна доставка или самовывоз.',
  'Уточняем детали по адресу и времени. Свяжемся в ближайшее время.',
];

export const STATUS_NOTE_TEMPLATE: Record<string, string> = {
  pending: 'Заказ принят. Менеджер проверяет детали и свяжется с вами в ближайшее время.',
  confirmed: 'Заказ подтвержден. Передали в производство.',
  in_production: 'Заказ в производстве. Сообщим, как только изделие будет готово.',
  ready: 'Изделие готово. Доступна доставка или самовывоз.',
  delivered: 'Заказ доставлен. Спасибо, что выбрали Onviz.',
  cancelled: 'Заказ отменен. Если нужно, подготовим новое предложение под ваш проект.',
};

export const STATUS_FLOW = ['pending', 'confirmed', 'in_production', 'ready', 'delivered'] as const;

export interface OrderUser {
  id: number;
  telegram_id: number;
  first_name: string;
  username?: string;
  role: string;
}

export interface OrderItem {
  id: number;
  product_snapshot: Record<string, unknown>;
  custom_params?: Record<string, unknown> | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  length_m?: number;
  width_m?: number;
}

export interface AdminOrder {
  id: number;
  order_number: string;
  status: string;
  sales_stage?: string;
  has_valid_open_task?: boolean;
  has_overdue_task?: boolean;
  order_has_pricing_drift?: boolean;
  total_amount: number;
  discount_pct: number;
  final_amount: number;
  notes?: string;
  manager_notes?: string;
  assigned_manager_id?: number | null;
  assigned_manager?: { id: number; first_name?: string; username?: string } | null;
  client_assigned_manager_id?: number | null;
  created_at: string;
  updated_at?: string;
  status_history?: Array<{ at: string; from?: string; to: string; manager_notes?: string | null }>;
  user: OrderUser;
  items: OrderItem[];
}

export interface ItemDraft {
  quantity: number;
  unit_price: number;
}

export interface NewItemDraft {
  name: string;
  quantity: number;
  unit_price: number;
  product_id?: number;
}

export type OrderScope = 'all' | 'mine' | 'unassigned';
export type MeasurementInterest = 'sliding' | 'roman' | 'roller' | 'smarthome' | '';
export type MeasurementSourceFilter = '' | 'Каталог' | 'Карточка решения' | 'Карточка товара';

export const INTEREST_LABELS: Record<string, string> = {
  sliding: 'Электрокарнизы',
  roman: 'Римские шторы',
  roller: 'Рулонные шторы',
  smarthome: 'Умный дом',
};

const MEASUREMENT_SOURCE_META: Array<{ key: string; label: string }> = [
  { key: 'из каталога', label: 'Каталог' },
  { key: 'из карточки решения', label: 'Карточка решения' },
  { key: 'из карточки товара', label: 'Карточка товара' },
];

export const ADMIN_MEAS_ACTION_GRID = 'grid grid-cols-2 gap-2';
export const ADMIN_MEAS_ACTION_BTN =
  'press flex items-center justify-center gap-1.5 min-h-[44px] px-3 py-2 rounded-xl text-xs font-bold transition-all disabled:opacity-40';

export const MEASUREMENT_STATUS_META: Record<string, { label: string; color: string; bg: string; border: string }> = {
  requested: { label: 'Принят', color: '#63B3ED', bg: 'rgba(99,179,237,0.15)', border: 'rgba(99,179,237,0.35)' },
  scheduled: { label: 'В работе', color: '#F6AD55', bg: 'rgba(246,173,85,0.15)', border: 'rgba(246,173,85,0.35)' },
  completed: { label: 'Завершён', color: '#68D391', bg: 'rgba(72,187,120,0.15)', border: 'rgba(72,187,120,0.35)' },
  cancelled: { label: 'Отменён', color: '#FC814A', bg: 'rgba(252,129,74,0.15)', border: 'rgba(252,129,74,0.35)' },
};

export function statusMeta(value: string) {
  return STATUSES.find((s) => s.value === value) ?? STATUSES[0];
}

export function nextStatuses(current: string): string[] {
  const idx = STATUS_FLOW.indexOf(current as (typeof STATUS_FLOW)[number]);
  if (idx === -1) return ['pending', 'confirmed'];
  return STATUS_FLOW.slice(idx + 1, idx + 3) as unknown as string[];
}

/** Объединяет snapshot позиции с custom_params (конфигуратор v2). */
export function mergeOrderItemSnapshot(
  snap: Record<string, unknown>,
  customParams?: Record<string, unknown> | null,
  dims?: { length_m?: number | null; width_m?: number | null },
): Record<string, unknown> {
  const cp = customParams && typeof customParams === 'object' ? customParams : {};
  const merged: Record<string, unknown> = { ...snap };
  for (const [key, val] of Object.entries(cp)) {
    if (val != null && merged[key] === undefined) merged[key] = val;
  }
  if (cp.v2_system_slug && !merged.system_slug) merged.system_slug = cp.v2_system_slug;
  const motorName = merged.motor_full_name ?? merged.motor_model;
  const nameStr = String(merged.name ?? '');
  if (
    cp.v2_system_title &&
    (!nameStr || nameStr === motorName || /^MR[\s-]/i.test(nameStr))
  ) {
    merged.name = cp.v2_system_title;
  }
  if (cp.v2_category && !merged.category) merged.category = cp.v2_category;
  if (dims?.length_m != null) merged.length_m = dims.length_m;
  if (dims?.width_m != null) merged.width_m = dims.width_m;
  return merged;
}

/** Заголовок позиции: тип изделия, не только код мотора. */
export function resolveOrderItemTitle(snap: Record<string, unknown>): string {
  if (!snap || typeof snap !== 'object') return 'Позиция';

  const isV2 = Boolean(snap.v2_system_slug || snap.system_slug);
  const systemTitle = String(snap.v2_system_title ?? snap.name ?? '').trim();
  const category = String(snap.v2_category ?? snap.category ?? '').trim();
  const subcategory = String(snap.subcategory ?? '').trim();

  if (isV2 && systemTitle) {
    const parts = [category, subcategory].filter(
      (p) => p && !systemTitle.toLowerCase().includes(p.toLowerCase()),
    );
    if (parts.length) return `${parts.slice(0, 2).join(' · ')} — ${systemTitle}`;
    return systemTitle || category || 'Система';
  }

  const productName = String(snap.name ?? '').trim();
  const motor = String(snap.motor_full_name ?? snap.motor_model ?? '').trim();
  const nameOk = productName && productName !== motor;
  const labelParts = [category, subcategory, nameOk ? productName : ''].filter(Boolean);
  if (labelParts.length) {
    const title = labelParts.slice(0, 3).join(' · ');
    if (!motor || title.includes(motor)) return title;
    return title;
  }
  if (productName) return productName;
  if (category && motor) return `${category} · ${motor}`;
  return motor || category || 'Позиция';
}

export function resolveOrderItemDetailLines(snap: Record<string, unknown>): string[] {
  const lines: string[] = [];
  const motor = snap.motor_full_name ?? snap.motor_model;
  const title = resolveOrderItemTitle(snap);
  if (motor && !String(title).includes(String(motor))) {
    lines.push(`Мотор: ${motor}`);
  }
  const protocol = snap.v2_protocol ?? snap.protocol;
  if (typeof protocol === 'string' && protocol) lines.push(`Протокол: ${protocol}`);
  const color = snap.v2_color ?? snap.color ?? snap.profile_color ?? snap.motor_color;
  if (typeof color === 'string' && color) lines.push(`Цвет: ${color}`);
  const opening = snap.opening_type;
  if (opening === 'center') lines.push('Открывание: к центру');
  else if (opening === 'left') lines.push('Открывание: влево');
  else if (opening === 'right') lines.push('Открывание: вправо');
  else if (typeof opening === 'string' && opening) lines.push(`Открывание: ${opening}`);
  if (snap.v2_price_on_request) lines.push('Цена: по запросу');
  lines.push(
    ...formatOrderDimensionLines({
      length_m: typeof snap.length_m === 'number' ? snap.length_m : null,
      width_m: typeof snap.width_m === 'number' ? snap.width_m : null,
      projection_m: typeof snap.projection_m === 'number' ? snap.projection_m : null,
      custom_params: snap,
    }),
  );
  const variant = snap.v2_variant_sku ?? snap.variant_sku;
  if (variant) lines.push(`SKU: ${variant}`);
  const tier = snap.v2_tier_slug ?? snap.tier_slug;
  if (tier) lines.push(`Линейка: ${tier}`);
  return lines;
}

export function formatSnapshotConfigLines(snap: Record<string, unknown>): string[] {
  return resolveOrderItemDetailLines(snap);
}

export function readSnapshotBreakdown(snap: Record<string, unknown>): {
  base: number | null;
  surcharges: Array<{ label: string; amount: number }>;
} {
  if (!snap) return { base: null, surcharges: [] };
  const baseRaw = Number(snap.v2_base_price);
  const base = Number.isFinite(baseRaw) ? baseRaw : null;
  const source = Array.isArray(snap.v2_surcharges) ? snap.v2_surcharges : [];
  const surcharges = source
    .map((s: { label?: string; amount?: number }) => ({
      label: String(s?.label ?? ''),
      amount: Number(s?.amount ?? 0),
    }))
    .filter((s) => s.label && Number.isFinite(s.amount) && s.amount > 0);
  return { base, surcharges };
}

export function managerDisplayName(m?: { first_name?: string | null; username?: string | null } | null): string {
  if (!m) return '';
  return (m.first_name || m.username || '').trim();
}

export function extractMeasurementSourceLabel(notes?: string): string | null {
  if (!notes) return null;
  const lowered = notes.toLowerCase();
  const match = MEASUREMENT_SOURCE_META.find((item) => lowered.includes(item.key));
  return match?.label ?? null;
}
